import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { type RefObject, useEffect, useMemo, useRef } from "react";
import { buildSpeakerTints } from "./speakerColors";
import {
  type TranscriptLineView,
  type TranscriptSpeakerRun,
  groupLinesByChannel,
  groupWordsBySpeaker,
} from "./transcriptSync";

dayjs.extend(utc);

type Props = Readonly<{
  lines: readonly TranscriptLineView[];
  activeWordKey: string | null;
  // Moves playback to a line's channel. Omitted where there is nothing to
  // switch to, which is also when lines carry no channel label.
  onChannelClick?: (receiverId: number) => void;
  // Moves the playhead to when a line was said. Omitted where playback cannot
  // be seeked, so the lines stay plain text rather than looking clickable.
  onLineClick?: (startMs: number, receiverId: number | null) => void;
}>;

// Heads the channel's block and stays put while its lines scroll under it, so
// the talkgroup a line belongs to is on screen however far into the block the
// reader is. Clicking it plays that channel.
const ChannelHeader = ({
  label,
  receiverId,
  lineCount,
  onClick,
}: Readonly<{
  label: string;
  receiverId: number | null;
  lineCount: number;
  onClick?: (receiverId: number) => void;
}>) => {
  const { t } = useTenantTranslation();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        position: "sticky",
        // Pulls up over the container's padding, so lines scroll under the
        // header rather than through the gap above it.
        top: -12,
        zIndex: 1,
        bgcolor: "background.paper",
        py: 0.5,
      }}
    >
      <Chip
        size="small"
        variant="outlined"
        color="primary"
        label={label}
        onClick={
          onClick && receiverId != null
            ? () => {
                onClick(receiverId);
              }
            : undefined
        }
        sx={{ flexShrink: 0 }}
      />
      <Typography variant="body2" color="text.secondary">
        {t("radioTranscriptTransmissionCount", { count: lineCount })}
      </Typography>
    </Stack>
  );
};

// The tooltip and aria-label carry the handset id so the tint is never the only
// cue. Deliberately not focusable: the surrounding line is already a seek button.
// A run several handsets talked over has no handset to tint, so it is underlined
// instead.
const SpeakerRun = ({
  run,
  tint,
  activeWordKey,
  activeWordRef,
}: Readonly<{
  run: TranscriptSpeakerRun;
  tint: string | undefined;
  activeWordKey: string | null;
  activeWordRef: RefObject<HTMLSpanElement | null>;
}>) => {
  const { t } = useTenantTranslation();
  const hasSeveralSpeakers = run.concurrentTransmissions > 1;
  const speakerLabel = hasSeveralSpeakers
    ? t("radioSpeakerConcurrent", { handsetCount: run.concurrentTransmissions })
    : run.sourceId == null
      ? t("radioSpeakerUnknown")
      : t("radioSpeakerHandset", { handsetId: run.sourceId });

  return (
    <Tooltip title={speakerLabel} placement="top">
      <Box
        component="span"
        aria-label={
          hasSeveralSpeakers || run.sourceId != null ? speakerLabel : undefined
        }
        sx={{
          bgcolor: tint ?? "transparent",
          borderRadius: 0.5,
          px: tint ? 0.5 : 0,
          textDecoration: hasSeveralSpeakers ? "underline dotted" : undefined,
          // Keeps the padding and rounding on both halves of a run that wraps.
          boxDecorationBreak: "clone",
          WebkitBoxDecorationBreak: "clone",
        }}
      >
        {run.words.map((word) => {
          const isActive = word.key === activeWordKey;
          return (
            <Box
              component="span"
              key={word.key}
              ref={isActive ? activeWordRef : undefined}
              sx={{
                mr: "0.28em",
                px: isActive ? 0.5 : 0,
                borderRadius: 0.5,
                // Sits over the speaker tint so playback still reads inside a run.
                bgcolor: isActive ? "primary.main" : "transparent",
                color: isActive ? "primary.contrastText" : "inherit",
                transition: "background-color 120ms linear",
              }}
            >
              {word.text}
            </Box>
          );
        })}
      </Box>
    </Tooltip>
  );
};

const TranscriptLine = ({
  line,
  speakerTints,
  activeWordKey,
  activeWordRef,
  onLineClick,
}: Readonly<{
  line: TranscriptLineView;
  speakerTints: ReadonlyMap<number, string>;
  activeWordKey: string | null;
  activeWordRef: RefObject<HTMLSpanElement | null>;
  onLineClick?: (startMs: number, receiverId: number | null) => void;
}>) => {
  const { t } = useTenantTranslation();

  return (
    <Box
      onClick={
        onLineClick
          ? () => {
              onLineClick(line.startMs, line.receiverId);
            }
          : undefined
      }
      onKeyDown={
        onLineClick
          ? (event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              onLineClick(line.startMs, line.receiverId);
            }
          : undefined
      }
      role={onLineClick ? "button" : undefined}
      tabIndex={onLineClick ? 0 : undefined}
      aria-label={onLineClick ? t("radioSeekToLine") : undefined}
      sx={{
        display: "flex",
        gap: 1.5,
        borderRadius: 1,
        mx: -0.5,
        px: 0.5,
        cursor: onLineClick ? "pointer" : "default",
        "&:hover": onLineClick ? { bgcolor: "action.hover" } : undefined,
        "&:focus-visible": { outline: 2, outlineColor: "primary.main" },
      }}
    >
      <Typography
        variant="body2Mono"
        color="text.secondary"
        sx={{ flexShrink: 0, pt: "1px" }}
      >
        {`${dayjs.utc(line.startMs).format("HH:mm:ss")} UTC`}
      </Typography>
      {line.language && (
        <Typography
          variant="body2Mono"
          color="text.secondary"
          sx={{ flexShrink: 0, pt: "1px" }}
        >
          {`[${line.language.toUpperCase()}]`}
        </Typography>
      )}
      <Typography variant="body2" component="p" sx={{ m: 0 }}>
        {groupWordsBySpeaker(line.words).map((run) => (
          <SpeakerRun
            key={run.key}
            run={run}
            tint={
              run.sourceId == null ? undefined : speakerTints.get(run.sourceId)
            }
            activeWordKey={activeWordKey}
            activeWordRef={activeWordRef}
          />
        ))}
      </Typography>
    </Box>
  );
};

export const TranscriptView = ({
  lines,
  activeWordKey,
  onChannelClick,
  onLineClick,
}: Props) => {
  const { t } = useTenantTranslation();
  const activeWordRef = useRef<HTMLSpanElement | null>(null);
  const groups = useMemo(() => groupLinesByChannel(lines), [lines]);
  // Spans the whole transcript so a handset heard on two channels keeps one tint.
  const speakerTints = useMemo(() => buildSpeakerTints(lines), [lines]);

  useEffect(() => {
    activeWordRef.current?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
  }, [activeWordKey]);

  if (lines.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("radioTranscriptEmpty")}
      </Typography>
    );
  }

  return (
    <Stack
      spacing={2}
      sx={{
        maxHeight: 320,
        overflowY: "auto",
        pr: 1,
        border: 1,
        borderColor: "grey.300",
        borderRadius: 1,
        p: 1.5,
      }}
    >
      {groups.map((group) => (
        <Stack key={group.key} spacing={1}>
          {group.channelLabel && (
            <ChannelHeader
              label={group.channelLabel}
              receiverId={group.receiverId}
              lineCount={group.lines.length}
              onClick={onChannelClick}
            />
          )}
          {/* The rule down the side ties a run of lines to the channel heading
              it sits under; dropped when the scope is one channel and there is
              no heading to tie them to. */}
          <Stack
            spacing={1}
            sx={
              group.channelLabel
                ? { borderLeft: 2, borderColor: "grey.200", pl: 1.5 }
                : undefined
            }
          >
            {group.lines.map((line) => (
              <TranscriptLine
                key={line.key}
                line={line}
                speakerTints={speakerTints}
                activeWordKey={activeWordKey}
                activeWordRef={activeWordRef}
                onLineClick={onLineClick}
              />
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};
